import { useState } from 'react';
import { Link } from 'react-router-dom';
// Annual billing only — price = monthly * 12 * 0.8
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 49,
    tagline: 'California Focus',
    popular: false,
    border: 'border-white/10',
    btnClass: 'bg-white/8 border border-white/12 text-white/70 hover:bg-white/15 hover:text-white',
    highlight: '',
    scope: 'California only',
    acquisitions: 'Up to 5/year',
    features: [
      { label: 'Managed certificate acquisitions',    value: '5 per year' },
      { label: 'Service coverage',                   value: 'California' },
      { label: 'Tax sale monitoring',                value: 'CA counties' },
      { label: 'Acquisition alerts',                 value: 'Weekly digest' },
      { label: 'Member dashboard',                   value: true },
      { label: 'Saved certificate slots',            value: '5 slots' },
      { label: 'Investment guides library',          value: 'Starter access' },
      { label: 'Tax records database',               value: false },
      { label: 'Priority pre-sale alerts',           value: false },
      { label: 'Monthly expert webinars',            value: false },
      { label: '1-on-1 strategy calls',              value: false },
      { label: 'White-glove support',                value: false },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 99,
    tagline: 'Most Popular',
    popular: true,
    border: 'border-gold',
    btnClass: 'bg-gold text-navy-dark hover:bg-gold-light',
    highlight: 'bg-gold',
    scope: 'CA + NV + AZ',
    acquisitions: 'Up to 20/year',
    features: [
      { label: 'Managed certificate acquisitions',    value: '20 per year' },
      { label: 'Service coverage',                   value: 'CA + NV + AZ' },
      { label: 'Tax sale monitoring',                value: 'All 3 states' },
      { label: 'Acquisition alerts',                 value: 'Daily priority' },
      { label: 'Member dashboard',                   value: true },
      { label: 'Saved certificate slots',            value: '50 slots' },
      { label: 'Investment guides library',          value: 'Full access' },
      { label: 'Tax records database',               value: true },
      { label: 'Priority pre-sale alerts',           value: '30–60 days early' },
      { label: 'Monthly expert webinars',            value: true },
      { label: '1-on-1 strategy calls',              value: false },
      { label: 'White-glove support',                value: false },
    ],
  },
  {
    id: 'elite',
    name: 'Elite',
    price: 199,
    tagline: 'White-Glove',
    popular: false,
    border: 'border-white/20',
    btnClass: 'bg-white/8 border border-white/20 text-white hover:bg-white/15',
    highlight: '',
    scope: 'CA + NV + AZ + custom',
    acquisitions: 'Unlimited',
    features: [
      { label: 'Managed certificate acquisitions',    value: 'Unlimited' },
      { label: 'Service coverage',                   value: 'CA + NV + AZ + custom' },
      { label: 'Tax sale monitoring',                value: 'All 3 states + custom' },
      { label: 'Acquisition alerts',                 value: 'Instant priority' },
      { label: 'Member dashboard',                   value: true },
      { label: 'Saved certificate slots',            value: 'Unlimited' },
      { label: 'Investment guides library',          value: 'Full + exclusive' },
      { label: 'Tax records database',               value: true },
      { label: 'Priority pre-sale alerts',           value: '60–90 days early' },
      { label: 'Monthly expert webinars',            value: true },
      { label: '1-on-1 strategy calls',              value: 'Monthly' },
      { label: 'White-glove support',                value: 'Dedicated specialist' },
    ],
  },
];

const FAQS = [
  { q: 'Do I need real estate experience?', a: 'None at all. Our specialists handle the entire acquisition process. You define your investment criteria and we execute — you\'re never required to attend a sale or file documents.' },
  { q: 'What\'s the difference between a Tax Lien and a Tax Deed?', a: 'Tax lien certificates (used in AZ) give you interest-bearing claim on a delinquent property. Tax deeds (CA & NV) transfer ownership directly. We handle both types across all three states.' },
  { q: 'How quickly can acquisitions begin after joining?', a: 'Immediately after your 14-day free trial starts. We configure your acquisition profile in 24-48 hours and begin monitoring eligible tax sales at once.' },
  { q: 'How does the 14-day trial work?', a: 'You get full access to your chosen service tier for 14 days at no charge. No credit card needed. You\'ll receive an email before the trial ends — only continue if you\'re satisfied.' },
  { q: 'Can I upgrade or cancel anytime?', a: 'Yes. Upgrade takes effect immediately. Cancel anytime before your next billing cycle — no cancellation fees, no questions asked.' },
  { q: 'What annual returns can I expect?', a: 'Arizona tax lien certificates carry state-mandated interest up to 16% per year. California and Nevada tax deed acquisitions vary by market but our members average 14.2% annual return across their portfolios.' },
];

export default function MembershipBenefits() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const price = (base: number) => Math.round(base * 12 * 0.8);

  return (
    <div className="font-sans bg-[#090b10]">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-40 pb-24 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-20 left-1/3 w-[500px] h-[500px] rounded-full bg-gold/4 blur-[120px]" />
          </div>
          <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 text-center">
            <span className="inline-block px-4 py-1.5 border border-gold/25 text-gold text-xs font-semibold tracking-widest uppercase rounded-full mb-6">
              Service Plans
            </span>
            <h1 className="font-playfair text-5xl md:text-6xl font-bold text-white mb-6">
              Managed Acquisition Services<br />Built Around You
            </h1>
            <p className="text-white/45 text-xl max-w-2xl mx-auto">
              Every tier includes fully managed certificate acquisition — the difference is scope, volume, and how hands-on you want us to be.
            </p>
          </div>
        </section>

        {/* Annual billing section */}
        <section className="pb-20">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <div className="flex justify-center mb-12">
              <div className="inline-flex items-center gap-2 bg-surface-2 border border-white/8 rounded-full px-6 py-2.5">
                <span className="px-2.5 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full whitespace-nowrap">Annual Billing</span>
                <span className="text-white/50 text-sm">Save 20% vs monthly</span>
              </div>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {TIERS.map((tier) => (
                <div key={tier.id} className={`relative bg-surface-2 border-2 rounded-2xl overflow-hidden ${tier.border} ${tier.popular ? 'md:-mt-4' : ''}`}>
                  {tier.popular && <div className="bg-gold text-navy-dark text-xs font-bold text-center py-2.5 tracking-widest uppercase">★ Most Popular</div>}
                  <div className="p-8">
                    <div className="flex items-start justify-between mb-2">
                      <h2 className="font-playfair text-2xl font-bold text-white">{tier.name}</h2>
                      {!tier.popular && <span className="text-xs text-white/25 border border-white/10 px-2 py-0.5 rounded-full">{tier.tagline}</span>}
                    </div>

                    <div className="flex items-center gap-2 text-xs text-white/30 mb-6">
                      <i className="ri-map-pin-line text-gold text-xs" />{tier.scope}
                      <span className="mx-1">·</span>
                      <i className="ri-coupon-line text-gold text-xs" />{tier.acquisitions}
                    </div>

                    <div className="mb-7">
                      <span className={`font-playfair text-5xl font-bold ${tier.popular ? 'text-gold' : 'text-white'}`}>
                        ${price(tier.price).toLocaleString()}
                      </span>
                      <span className="text-white/25 text-sm">/yr</span>
                      <p className="text-green-400 text-xs mt-1">Billed annually · saves ${Math.round(tier.price * 12 * 0.2).toLocaleString()}</p>
                    </div>

                    <Link to="/signup" className={`block text-center py-3.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap mb-7 ${tier.btnClass}`}>
                      Start Free Trial
                    </Link>

                    <ul className="flex flex-col gap-3">
                      {tier.features.map((f) => (
                        <li key={f.label} className="flex items-start gap-3">
                          {f.value === false ? (
                            <i className="ri-subtract-line text-white/15 text-sm flex-shrink-0 mt-0.5" />
                          ) : (
                            <i className="ri-check-line text-gold text-sm flex-shrink-0 mt-0.5" />
                          )}
                          <span className={`text-sm leading-relaxed ${f.value === false ? 'text-white/20' : 'text-white/55'}`}>
                            {f.label}
                            {f.value !== true && f.value !== false && (
                              <span className="text-white/80 font-medium ml-1">— {f.value}</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-white/20 text-xs text-center mt-6 pt-5 border-t border-white/5">
                      14-day free trial · Billed annually
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Money-back */}
            <div className="flex items-center justify-center gap-3 mb-20">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-green-500/10 flex-shrink-0">
                <i className="ri-shield-check-line text-green-400 text-base" />
              </div>
              <p className="text-white/40 text-sm">
                <strong className="text-white/70">30-day money-back guarantee.</strong> Not satisfied? We refund in full — no questions asked.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-surface-1 border-t border-white/5 py-20">
          <div className="max-w-[800px] mx-auto px-6 md:px-10">
            <div className="text-center mb-12">
              <h2 className="font-playfair text-4xl font-bold text-white">Frequently Asked Questions</h2>
            </div>
            <div className="flex flex-col gap-3">
              {FAQS.map((faq, idx) => (
                <div key={idx} className="bg-surface-2 border border-white/8 rounded-xl overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer">
                    <span className="font-semibold text-white/80 text-sm pr-4">{faq.q}</span>
                    <i className={`ri-${openFaq === idx ? 'subtract' : 'add'}-line text-gold text-xl flex-shrink-0`} />
                  </button>
                  {openFaq === idx && (
                    <div className="px-6 pb-5 border-t border-white/5">
                      <p className="text-white/45 text-sm leading-relaxed pt-4">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-5">Start Building Your Portfolio Today</h2>
            <p className="text-white/40 text-lg mb-8">14-day free trial. No credit card. Full managed service from day one.</p>
            <Link to="/signup" className="inline-flex items-center gap-2 px-10 py-4 bg-gold text-navy-dark font-bold rounded-xl hover:bg-gold-light transition-colors whitespace-nowrap text-lg">
              Start Free Trial <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
